import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ShoppingBag,
  Sparkles,
  ShieldCheck,
  Tag,
  Check,
} from "lucide-react";

import serumImg from "../assests/pro4.webp"; // Path verify kar lein

interface CartItem {
  id: string;
  name: string;
  category: string;
  price: number;
  quantity: number;
  image: string;
  size: string;
}

const Cart: React.FC = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([
    {
      id: "glow-serum",
      name: "Glow Niacinamide Serum",
      category: "Serums & Oils",
      price: 48.0,
      quantity: 1,
      image: serumImg,
      size: "50ml / 1.7 fl. oz.",
    },
    {
      id: "barrier-cream",
      name: "Ceramide Hydrating Barrier Cream",
      category: "Moisturizers",
      price: 52.0,
      quantity: 2,
      image: serumImg,
      size: "60g / 2.0 oz.",
    },
  ]);

  const [couponCode, setCouponCode] = useState<string>("");
  const [discount, setDiscount] = useState<number>(0);
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string>("");

  const updateQuantity = (id: string, delta: number): void => {
    setCartItems((prevItems) =>
      prevItems
        .map((item) => {
          if (item.id === id) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter((item): item is CartItem => item !== null)
    );
  };

  const removeItem = (id: string): void => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleApplyCoupon = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setCouponError("");

    if (couponCode.trim().toUpperCase() === "CELIORA20") {
      setDiscount(0.2);
      setCouponApplied(true);
    } else {
      setCouponError("Invalid coupon code. Try CELIORA20");
    }
  };

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );
  const discountAmount = subtotal * discount;
  const freeShippingThreshold = 120.0;
  const shipping = subtotal >= freeShippingThreshold || subtotal === 0 ? 0 : 15.0;
  const grandTotal = subtotal - discountAmount + shipping;

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-12 px-4 sm:px-6 lg:px-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-8">
        
        {/* Title */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#E8E2D9] pb-6">
          <div>
            <div className="flex items-center gap-2 text-[#9D7D4F] text-xs font-semibold uppercase tracking-[2px] mb-1">
              <Sparkles size={13} /> Your Selection
            </div>
            <h1 className="font-serif font-bold text-3xl sm:text-4xl text-[#1F1B18]">
              Shopping Bag
            </h1>
          </div>
          <p className="text-xs text-gray-500 font-medium">
            {cartItems.reduce((sum, item) => sum + item.quantity, 0)} Items
          </p>
        </div>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-[#E8E2D9] shadow-sm flex flex-col items-center justify-center space-y-4 my-8">
            <div className="w-16 h-16 rounded-full bg-[#F7F4EF] border border-[#E8E2D9] flex items-center justify-center text-[#9D7D4F]">
              <ShoppingBag size={28} />
            </div>
            <h3 className="font-serif font-semibold text-xl text-[#1F1B18]">
              Your Bag is Empty
            </h3>
            <p className="text-xs text-gray-500 max-w-sm leading-relaxed">
              Looks like you haven't added any luxury skincare items yet.
            </p>
            <Link
              to="/shop"
              className="mt-2 inline-flex items-center gap-2 bg-[#1F1B18] hover:bg-[#9D7D4F] text-white px-6 py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition"
            >
              <span>Continue Shopping</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Items */}
            <div className="lg:col-span-7 space-y-4">
              <div className="bg-white p-4 rounded-2xl border border-[#E8E2D9] shadow-sm space-y-2">
                <div className="flex justify-between text-xs font-medium">
                  <span className="text-[#1F1B18]">
                    {subtotal >= freeShippingThreshold
                      ? "🎉 You've unlocked Complimentary Shipping!"
                      : `Add $${(freeShippingThreshold - subtotal).toFixed(2)} more for Free Shipping`}
                  </span>
                  <span className="text-[#9D7D4F] font-semibold">
                    ${subtotal.toFixed(0)} / ${freeShippingThreshold}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-[#F7F4EF] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#9D7D4F] transition-all duration-500"
                    style={{
                      width: `${Math.min(
                        (subtotal / freeShippingThreshold) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-[#E8E2D9] shadow-sm divide-y divide-[#E8E2D9]/60 overflow-hidden">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl bg-[#F7F4EF] overflow-hidden flex-shrink-0 border border-[#E8E2D9]">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold block mb-0.5">
                          {item.category}
                        </span>
                        <h4 className="font-serif font-semibold text-base text-[#1F1B18]">
                          {item.name}
                        </h4>
                        <p className="text-[11px] text-gray-400 mt-0.5">{item.size}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between w-full sm:w-auto gap-6">
                      <div className="flex items-center border border-[#E8E2D9] rounded-xl bg-[#FAF8F5] p-1">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="p-1 hover:bg-white rounded-lg transition"
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-8 text-center text-xs font-semibold text-[#1F1B18]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="p-1 hover:bg-white rounded-lg transition"
                        >
                          <Plus size={13} />
                        </button>
                      </div>

                      <p className="hidden sm:block text-sm font-semibold text-[#1F1B18] w-20 text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center gap-2 text-[11px] text-gray-400 uppercase tracking-widest pt-2">
                <ShieldCheck size={15} className="text-[#9D7D4F]" /> Guaranteed Safe & Secure Checkout
              </div>
            </div>

            {/* Right Summary */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E8E2D9] shadow-sm space-y-6">
                <h3 className="font-serif font-semibold text-xl text-[#1F1B18] border-b border-[#E8E2D9] pb-4">
                  Order Summary
                </h3>

                <form onSubmit={handleApplyCoupon} className="space-y-2">
                  <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold">
                    Promo Code
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="e.g. CELIORA20"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        disabled={couponApplied}
                        className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 pl-9 pr-3 text-xs uppercase tracking-wider focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                      />
                      <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <button
                      type="submit"
                      disabled={couponApplied || !couponCode.trim()}
                      className="bg-[#1F1B18] hover:bg-[#9D7D4F] text-white px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition"
                    >
                      {couponApplied ? <Check size={14} /> : "Apply"}
                    </button>
                  </div>
                  {couponError && <p className="text-[11px] text-red-500 mt-1">{couponError}</p>}
                </form>

                <div className="space-y-3 text-xs border-t border-b border-[#E8E2D9] py-4 text-gray-600">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-semibold text-[#1F1B18]">${subtotal.toFixed(2)}</span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Discount (20%)</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Estimated Shipping</span>
                    <span className="font-semibold text-[#1F1B18]">
                      {shipping === 0 ? "Complimentary" : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-end pt-1">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-gray-400 block">Total Due</span>
                    <span className="font-serif text-2xl font-bold text-[#1F1B18]">
                      ${grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => alert("Proceeding to Secure Checkout...")}
                  className="w-full bg-[#1F1B18] hover:bg-[#9D7D4F] text-white py-3.5 rounded-xl text-xs font-semibold uppercase tracking-[2px] transition shadow-md flex items-center justify-center gap-2"
                >
                  <span>Proceed to Checkout</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Cart;